import { useState, useRef, useCallback, useEffect } from 'react';
import { FiMail, FiRefreshCw } from 'react-icons/fi';

import { VerificationLogoutButton } from '@/shared/components/VerificationLogoutButton';

interface VerificationOverlayProps {
  type: 'email' | 'phone';
  maskedTarget: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  error?: string;
  isVerifying?: boolean;
  onLogout: () => void;
}

export function VerificationOverlay({
  type,
  maskedTarget,
  onVerify,
  onResend,
  error,
  isVerifying,
  onLogout,
}: VerificationOverlayProps) {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    setCountdown(60);
  }, []);

  const handleChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        onVerify(code);
      }
    }
  }, [digits, onVerify]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, [digits]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
      onVerify(pasted);
    }
  }, [onVerify]);

  const handleResend = useCallback(async () => {
    if (countdown > 0 || resending) return;
    setResending(true);
    try {
      await onResend();
      setCountdown(60);
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setResending(false);
    }
  }, [countdown, resending, onResend]);

  const title = type === 'email' ? 'Verifique seu email' : 'Verifique seu celular';
  const description = type === 'email'
    ? `Enviamos um código de 6 dígitos para ${maskedTarget}`
    : `Enviamos um código de 6 dígitos para ${maskedTarget}`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '420px',
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        <VerificationLogoutButton onLogout={onLogout} disabled={isVerifying} />
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#f3f0ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}
        >
          <FiMail style={{ width: '28px', height: '28px', color: '#8b5cf6' }} />
        </div>

        <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 600, color: '#1e293b' }}>
          {title}
        </h2>
        <p style={{ margin: '0 0 32px', fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
          {description}
        </p>

        {/* Code inputs — native <input> justified: specialized single-digit PIN input
            with auto-advance behavior, not supported by shared Input component.
            Hardcoded light theme styles (overlay is always light). */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '16px' }}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={i === 0 ? handlePaste : undefined}
              disabled={isVerifying}
              style={{
                width: '48px',
                height: '56px',
                textAlign: 'center',
                fontSize: '24px',
                fontWeight: 600,
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                outline: 'none',
                color: '#1e293b',
                backgroundColor: isVerifying ? '#f8fafc' : '#ffffff',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
            />
          ))}
        </div>

        {error && (
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#ef4444' }}>
            {error}
          </p>
        )}

        {isVerifying && (
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#8b5cf6' }}>
            Verificando...
          </p>
        )}

        <div style={{ marginTop: '24px' }}>
          {countdown > 0 ? (
            <p style={{ fontSize: '13px', color: '#94a3b8' }}>
              Reenviar código em {countdown}s
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                color: '#8b5cf6',
                fontSize: '14px',
                fontWeight: 500,
                cursor: resending ? 'not-allowed' : 'pointer',
                opacity: resending ? 0.6 : 1,
              }}
            >
              <FiRefreshCw style={{ width: '14px', height: '14px' }} />
              {resending ? 'Reenviando...' : 'Reenviar código'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
